import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HELICOPTER_PACKAGES } from "@/types/helicopter";
import { toast } from "sonner";

interface HelicopterBookingFormProps {
  selectedPackageId?: string;
}

const HelicopterBookingForm = ({ selectedPackageId }: HelicopterBookingFormProps) => {
  const [packageId, setPackageId] = useState<string>(selectedPackageId || "");
  const [guests, setGuests] = useState<number>(1);
  const [date, setDate] = useState<Date>();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const selectedPackage = HELICOPTER_PACKAGES.find(p => p.id === packageId);
  
  // Calculate total price based on guests
  const calculateTotalPrice = () => {
    if (!selectedPackage) return 0;
    // Grand Explorer is private only - fixed price
    if (selectedPackage.isPrivateOnly) {
      return selectedPackage.price;
    }
    // For other packages, price per guest
    return selectedPackage.price * guests;
  };

  const totalPrice = calculateTotalPrice();

  const handleDateSelect = (selectedDate: Date | undefined) => {
    setDate(selectedDate);
    setIsCalendarOpen(false);
  };

  const handleBookNow = () => {
    if (!packageId || !guests || !date) {
      toast.error("Please fill in all fields");
      return;
    }

    const formattedDate = format(date, "PPP");
    const message = `Hello! I'd like to book a Helicopter Ride.\n\n` +
      `🚁 Package: ${selectedPackage?.name}\n` +
      `⏱ Duration: ${selectedPackage?.duration}\n` +
      `👥 Guests: ${guests}\n` +
      `📅 Date: ${formattedDate}\n` +
      `💰 Total: AED ${totalPrice.toLocaleString()}\n\n` +
      `Please confirm availability. Thank you!`;

    const whatsappUrl = `https://wa.me/971504209377?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="bg-background/95 backdrop-blur-sm rounded-lg p-6 shadow-xl border">
      <h2 className="text-xl font-bold text-primary mb-1">Book Your Helicopter Ride</h2>
      <p className="text-sm text-muted-foreground mb-6">Experience Dubai from above</p>

      <div className="space-y-4">
        {/* Package Select */}
        <div>
          <label className="block text-sm font-medium mb-2">Select Package</label>
          <Select value={packageId} onValueChange={setPackageId}>
            <SelectTrigger className="w-full bg-background">
              <SelectValue placeholder="Choose a package" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              {HELICOPTER_PACKAGES.map((pkg) => (
                <SelectItem key={pkg.id} value={pkg.id}>
                  {pkg.name} - {pkg.duration}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Guests Select */}
        <div>
          <label className="block text-sm font-medium mb-2">Guests</label>
          <Select 
            value={guests.toString()} 
            onValueChange={(v) => setGuests(parseInt(v))}
            disabled={selectedPackage?.isPrivateOnly}
          >
            <SelectTrigger className="w-full bg-background">
              <SelectValue placeholder="Number of guests" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                <SelectItem key={num} value={num.toString()}>
                  {num} {num === 1 ? 'Guest' : 'Guests'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedPackage?.isPrivateOnly && (
            <p className="text-xs text-muted-foreground mt-1">Private flight only - up to 5 guests included</p>
          )}
        </div>

        {/* Date Picker */}
        <div>
          <label className="block text-sm font-medium mb-2">Travel Date</label>
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal bg-background",
                  !date && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, "PPP") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-background z-50" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={handleDateSelect}
                disabled={(date) => date < new Date()}
                initialFocus
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Price Summary */}
        {selectedPackage && (
          <div className="bg-secondary/20 rounded-lg p-4 mt-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Total Amount</span>
              <span className="text-2xl font-bold text-primary">AED {totalPrice.toLocaleString()}</span>
            </div>
            {!selectedPackage.isPrivateOnly && (
              <p className="text-xs text-muted-foreground mt-1">
                AED {selectedPackage.price.toLocaleString()} × {guests} guest{guests > 1 ? 's' : ''}
              </p>
            )}
          </div>
        )}

        {/* Book Now Button */}
        <Button 
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-6"
          onClick={handleBookNow}
          disabled={!packageId || !guests || !date}
        >
          Book Now
        </Button>
      </div>
    </div>
  );
};

export default HelicopterBookingForm;
